import React, { useState } from 'react';
import { FileText } from 'lucide-react';

export const MANUAL_SOAP_REASONS = ['Teletherapy', 'ABA / Center-Based', 'Other Exception'] as const;
export type ManualSoapReason = typeof MANUAL_SOAP_REASONS[number];

export function canEditManualSoapApproval(role: string | null | undefined): boolean {
  return role === 'ADMIN' || role === 'OFFICE' || role === 'OWNER';
}

export function formatManualSoapApprovedAt(value: unknown): string {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : text;
}

export function manualSoapApprovalDisplay(assignment: {
  manual_soap_approved?: unknown;
  manual_soap_reason?: unknown;
  manual_soap_approved_by?: unknown;
  manual_soap_approved_at?: unknown;
} | null | undefined) {
  const approved = Boolean(assignment?.manual_soap_approved);
  return {
    approved,
    statusLabel: approved ? 'Approved' : 'Disabled',
    reason: approved ? String(assignment?.manual_soap_reason || '') : '',
    approvedBy: approved ? String(assignment?.manual_soap_approved_by || '') : '',
    approvedAt: approved ? formatManualSoapApprovedAt(assignment?.manual_soap_approved_at) : ''
  };
}

export function manualSoapApprovalPayload(input: { approved: boolean; reason: string; note: string }) {
  return {
    approved: input.approved,
    reason: input.approved ? input.reason : null,
    note: input.approved ? input.note : null
  };
}

export interface ManualSoapApprovalAssignment {
  id: string;
  client_first_name?: string;
  client_last_name?: string;
  manual_soap_approved?: unknown;
  manual_soap_reason?: string | null;
  manual_soap_note?: string | null;
}

export const ManualSoapApprovalModal: React.FC<{
  assignment: ManualSoapApprovalAssignment;
  clientLabel?: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}> = ({ assignment, clientLabel, onClose, onSaved }) => {
  const [approved, setApproved] = useState(Boolean(assignment.manual_soap_approved));
  const [reason, setReason] = useState<ManualSoapReason>(
    (MANUAL_SOAP_REASONS as readonly string[]).includes(String(assignment.manual_soap_reason || ''))
      ? assignment.manual_soap_reason as ManualSoapReason
      : 'Teletherapy'
  );
  const [note, setNote] = useState(assignment.manual_soap_note || '');
  const [saving, setSaving] = useState(false);
  const label = clientLabel
    || [assignment.client_last_name, assignment.client_first_name].filter(Boolean).join(', ');

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignment.id) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/assignments/${assignment.id}/manual-soap-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(manualSoapApprovalPayload({ approved, reason, note }))
      });
      if (res.ok) {
        await onSaved();
      } else {
        const data = await res.json().catch(() => ({}));
        alert('Failed to update manual SOAP approval: ' + (data.error || 'Server error'));
      }
    } catch (err) {
      console.error('Error saving manual SOAP approval:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-[#f8fafc] text-slate-800 shadow-xl overflow-hidden">
        <div className="flex justify-between items-start px-6 py-4 bg-gradient-to-r from-[#2a394d] via-[#1a2a42] to-[#0d1727] border-b border-slate-700/80">
          <div>
            <h3 className="text-base font-bold text-white flex items-center space-x-2">
              <FileText className="w-5 h-5 text-amber-200" />
              <span>Manual SOAP / Remote Signature</span>
            </h3>
            <p className="text-xs text-slate-300 mt-1">
              Office/Admin exception approval{label ? ` for ${label}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-300 hover:text-white text-lg font-bold"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSave} className="space-y-4 text-xs p-6">
          <div className="p-3 bg-white border border-slate-200 rounded-lg text-slate-700 space-y-1 text-[11px] shadow-xs">
            <div className="font-semibold text-slate-900">Standard: Mobile SOAP with In-Person Signature</div>
            <div className="text-slate-600">
              Routine treatment notes must proceed through Mobile SOAP.
              Manual desktop SOAP with SignWell remote dispatch is an exception lane requiring specific operational approval.
            </div>
          </div>

          <div>
            <label className="block text-slate-800 font-semibold mb-2">Exception Capability Status</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setApproved(false)}
                className={`p-2.5 rounded-lg border text-left font-medium transition-all ${
                  !approved
                    ? 'bg-slate-100 border-slate-400 text-slate-900 shadow-xs'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="font-bold">Disabled</div>
                <div className="text-[10px] text-slate-600 mt-0.5">Mobile SOAP standard only</div>
              </button>
              <button
                type="button"
                onClick={() => setApproved(true)}
                className={`p-2.5 rounded-lg border text-left font-medium transition-all ${
                  approved
                    ? 'bg-amber-50 border-amber-300 text-amber-900 shadow-xs'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className={`font-bold ${approved ? 'text-amber-900' : 'text-slate-800'}`}>Approved</div>
                <div className={`text-[10px] mt-0.5 ${approved ? 'text-amber-800' : 'text-slate-600'}`}>Permit manual SOAP + SignWell</div>
              </button>
            </div>
          </div>

          {approved && (
            <div className="space-y-3 pt-2 border-t border-slate-200">
              <div>
                <label className="block text-slate-800 font-semibold mb-1.5">
                  Approved Exception Reason <span className="text-rose-700">*</span>
                </label>
                <div className="space-y-1.5">
                  {MANUAL_SOAP_REASONS.map((item) => (
                    <label
                      key={item}
                      className={`flex items-center space-x-2 p-2 rounded-lg border cursor-pointer transition-all ${
                        reason === item
                          ? 'bg-amber-50 border-amber-300 text-slate-900'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="soapReason"
                        value={item}
                        checked={reason === item}
                        onChange={() => setReason(item)}
                        className="text-amber-600 focus:ring-amber-500"
                      />
                      <span className="font-medium text-xs">{item}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-slate-700 mb-1">Optional Operational Note</label>
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g., Guardian works offsite during therapy sessions; authorized by Office."
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-amber-500 text-xs"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-bold transition-colors"
            >
              {saving ? 'Saving...' : 'Save Exception Policy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
